/**
 * WelcomePage
 *
 * A landing page shown to users before authentication.
 * Displays the product introduction, navigation header, hero section,
 * feature highlights, and footer links.
 *
 * Provides navigation to:
 * - Sign in
 * - Sign up
 * - Documentation
 * - Resources
 *
 * @returns The public welcome page layout.
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AUTH_KEYS = [
  "authenticated",
  "userId",
  "session_token",
  "userName",
  "userEmail",
];

const hydrateSessionStorage = () => {
  AUTH_KEYS.forEach((key) => {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (!sessionValue && localValue) {
      sessionStorage.setItem(key, localValue);
    }
  });
};

const isUserLoggedIn = () => {
  hydrateSessionStorage();

  const isAuthenticated =
    sessionStorage.getItem("authenticated") === "true" ||
    localStorage.getItem("authenticated") === "true";

  const sessionToken =
    sessionStorage.getItem("session_token") ||
    localStorage.getItem("session_token");

  return isAuthenticated && Boolean(sessionToken);
};

const WelcomePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isUserLoggedIn()) {
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      <main className="flex-1">
        {/* HERO */}
  <section className="relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-blue-100/40 blur-3xl rounded-full" />

          <div className="relative max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between py-24 px-6 gap-16">

            {/* Left */}
            <div className="max-w-xl text-center lg:text-left">

              <span className="inline-flex items-center rounded-full bg-blue-100 px-4 py-1 text-sm font-semibold text-blue-700 mb-6">
                AI Debugging Platform
              </span>

              <h1 className="text-5xl md:text-6xl font-extrabold leading-tight tracking-tight">
                Find and fix
                <span className="block text-blue-600">
                  AI failures faster.
                </span>
              </h1>

              <p className="mt-6 text-lg leading-8 text-slate-600">
                Analyze outputs, trace root causes, and apply targeted fixes with
                explainable AI guidance.
              </p>

              <div className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
                {[
                  "Root Cause Analysis",
                  "Explainable Insights",
                  "Fast Iteration",
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium shadow-sm"
                  >
                    ✓ {item}
                  </span>
                ))}
              </div>

            </div>

            {/* Right */}
            <div className="w-full lg:w-[52%]">

              <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-2xl">

                <video
                  src="/demo.mp4"
                  controls
                  muted
                  loop
                  playsInline
                  className="w-full rounded-2xl"
                />

              </div>

            </div>

          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-20 grid sm:grid-cols-3 gap-8 text-center">
          <OutcomeCard
            title="Analyze"
            text="Run evaluations and spot failure patterns."
            iconSrc="/Analyze Icon 1.png"
          />
          <OutcomeCard
            title="Diagnose"
            text="Surface likely root causes with explainable signals."
            iconSrc="/Diagnose Icon.png"
          />
          <OutcomeCard
            title="Fix"
            text="Apply targeted changes and verify gains."
            iconSrc="/Fix Icon.png"
          />
        </section>
      </main>
    </div>
  );
};

const OutcomeCard = ({ title, text, iconSrc }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 shadow-sm hover:shadow-md transition">
    <div className="flex justify-center mb-2">
      <img src={iconSrc} alt={`${title} icon`} className="h-14 w-auto" />
    </div>
    <h3 className="text-xl font-semibold mb-2 text-[#0F172A]">{title}</h3>
    <p className="text-[#334155]">{text}</p>
  </div>
);

export default WelcomePage;
